import { useListContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";

import { MobileListPage } from "../layout/MobileListPage";
import { PROJECT_STAGES, PROJECT_STATUSES } from "./ProjectInputs";

type Project = {
  id: number;
  name: string;
  status: string;
  stage: string;
  due_date: string | null;
};

const nameOf = (choices: { id: string; name: string }[], id: string) =>
  choices.find((c) => c.id === id)?.name ?? id;

/**
 * Projects on a phone as cards, not a table.
 *
 * The shared desktop table put name, stage, status, company and two dates in a
 * row, which at phone width leaves two columns visible and everything else
 * behind a sideways swipe. A card shows the same fields stacked, in the order
 * they are actually read.
 */
const ProjectCards = () => {
  const { data, isPending } = useListContext<Project>();
  if (isPending) return null;
  const projects = data ?? [];

  if (projects.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No projects yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {projects.map((project) => (
        <Link
          key={project.id}
          to={`/projects/${project.id}/show`}
          className="block rounded-lg border p-3 active:bg-accent/50"
        >
          <div className="font-medium text-sm">{project.name}</div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
            <Badge variant="outline">
              {nameOf(PROJECT_STAGES, project.stage)}
            </Badge>
            <Badge variant="secondary">
              {nameOf(PROJECT_STATUSES, project.status)}
            </Badge>
            {project.due_date ? (
              <span className="text-xs text-muted-foreground">
                due {new Date(project.due_date).toLocaleDateString()}
              </span>
            ) : null}
          </div>
        </Link>
      ))}
    </div>
  );
};

export const MobileProjectList = () => {
  const translate = useTranslate();
  return (
    <List
      actions={false}
      title={false}
      sort={{ field: "created_at", order: "DESC" }}
      pagination={<ListPagination rowsPerPageOptions={[25, 50]} />}
    >
      <MobileListPage
        searchPlaceholder={translate("resources.projects.name", {
          smart_count: 2,
          _: "Projects",
        })}
      >
        <ProjectCards />
      </MobileListPage>
    </List>
  );
};

export default MobileProjectList;
